<template lang="pug">
.mailing-address-page
  .page-container
    h1.headline Mailing Address

    .intro-text
      p Please provide your mailing address for tax receipt purposes. This information is required to complete your Giving Wallet setup.

    el-form(
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      v-loading="isLoading"
    )
      el-form-item(label="Street Address" prop="street")
        el-input(
          v-model="form.street"
          placeholder="Enter street address"
        )

      el-form-item(label="City" prop="city")
        el-input(
          v-model="form.city"
          placeholder="Enter city"
        )

      .row
        .col-md-6
          el-form-item(label="State" prop="state")
            el-input(
              v-model="form.state"
              placeholder="Enter state"
            )
        .col-md-6
          el-form-item(label="ZIP Code" prop="zipCode")
            el-input(
              v-model="form.zipCode"
              placeholder="Enter ZIP code"
            )

      el-form-item(label="Country" prop="country")
        el-input(
          v-model="form.country"
          placeholder="Enter country"
        )

      .form-actions
        el-button(
          type="primary"
          @click="handleSubmit"
          :loading="isSaving"
        ).submit-btn Save Address

        el-button(
          @click="handleSkip"
        ).skip-btn Skip for Now

    .info-message
      p You can update this information later in your wallet settings.
</template>

<script lang="ts">
import {
  defineComponent, ref, reactive, computed, onMounted,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import webAppToast from '@/utils/webAppToast';
import type { FormInstance, FormRules } from 'element-plus';

export default defineComponent({
  name: 'MailingAddress',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const formRef = ref<FormInstance>();
    const isLoading = ref(false);
    const isSaving = ref(false);
    const orgId = ref<string | null>(null);

    // Extract orgId from query params
    if (route.query.orgId) {
      orgId.value = route.query.orgId as string;
    }

    const form = reactive({
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
    });

    const rules: FormRules = {
      street: [
        { required: true, message: 'Please enter street address', trigger: 'blur' },
      ],
      city: [
        { required: true, message: 'Please enter city', trigger: 'blur' },
      ],
      state: [
        { required: true, message: 'Please enter state', trigger: 'blur' },
      ],
      zipCode: [
        { required: true, message: 'Please enter ZIP code', trigger: 'blur' },
        { pattern: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code', trigger: 'blur' },
      ],
      country: [
        { required: true, message: 'Please enter country', trigger: 'blur' },
      ],
    };

    const handleSubmit = async () => {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        isSaving.value = true;

        // TODO: Implement API call to save mailing address
        // If orgId is present, save for that organization, otherwise save for user
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated API call

        webAppToast.success('Mailing address saved successfully');

        // Navigate to supported causes to show the updated wallet
        if (orgId.value) {
          router.push({
            path: '/web/supported-causes',
            query: { orgId: orgId.value },
          });
        } else {
          router.push('/web/wallet/dashboard');
        }
      } catch (error) {
        console.error('[MAILING ADDRESS ERROR]', error);
      } finally {
        isSaving.value = false;
      }
    };

    const handleSkip = () => {
      // Navigate to supported causes to show the wallet
      if (orgId.value) {
        router.push({
          path: '/web/supported-causes',
          query: { orgId: orgId.value },
        });
      } else {
        router.push('/web/wallet/dashboard');
      }
    };

    onMounted(() => {
      // Component mounted
    });

    return {
      formRef,
      form,
      rules,
      isLoading,
      isSaving,
      handleSubmit,
      handleSkip,
    };
  },
});
</script>

<style lang="scss" scoped>
.mailing-address-page {
  padding: 26px 20px 100px;
  background: #fff;
  min-height: 100vh;
}

.page-container {
  max-width: 600px;
  margin: 0 auto;
}

.headline {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #111;
}

.intro-text {
  margin-bottom: 32px;
  font-size: 16px;
  line-height: 1.6;
  color: #666;

  p {
    margin: 0;
  }
}

.form-actions {
  display: flex;
  gap: 16px;
  margin-top: 32px;

  .submit-btn {
    flex: 1;
    padding: 16px 34px;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    height: auto;
  }

  .skip-btn {
    padding: 16px 34px;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    height: auto;
  }
}

.info-message {
  margin-top: 24px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  text-align: center;

  p {
    margin: 0;
  }
}

@media (max-width: 768px) {
  .mailing-address-page {
    padding: 20px 16px 80px;
  }

  .headline {
    font-size: 28px;
  }

  .form-actions {
    flex-direction: column;

    .submit-btn,
    .skip-btn {
      width: 100%;
    }
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #333;
}

:deep(.el-input__inner) {
  border-radius: 8px;
}
</style>
